import { FC, useContext, useMemo } from "react";
import { QesActiveFiltersBar } from "@app/shared/QesActiveFiltersBar";
import type { FilterChipDescriptor } from "@app/shared/QesActiveFiltersBar";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { SERVICE_AREA_STATUS_DISPLAY_ORDER } from "@app/projects/network-assessment/serviceAreaStatusConstants";
import { ProviderActionStatusEnumToDisplay } from "@app/projects/utility";
import { MembershipType } from "@app/exports/enumerations";
import { ProviderSearchByEnum, ProviderViewByEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import { niHideLabels, providerNetworkTypeDisplayName, scoreFilterValueToDisplay } from "@app/projects/network-assessment/provider-hub/utility";

const TRUNCATE_AT = 20;

function makeChip(content: string, group: string, key: string, onDismiss: () => void, subgroup?: string): FilterChipDescriptor {
  return { content, group, key, onDismiss, subgroup, truncateAt: TRUNCATE_AT };
}

export const ProviderHubAppliedFilters: FC = () => {
  const providerHubContext = useContext(ProviderHubContext);

  const chips = useMemo<FilterChipDescriptor[]>(() => {
    const result: FilterChipDescriptor[] = [];
    const filterState = providerHubContext.filterState;
    if (!filterState) return result;

    // Membership Type
    if (providerHubContext.showMembershipTypeDropdown && filterState.membershipType !== providerHubContext.initialFilterState!.membershipType) {
      const displayName = filterState.membershipType === MembershipType.Primary
        ? providerHubContext.membershipAliases?.primary ?? "Primary"
        : providerHubContext.membershipAliases?.secondary ?? "Secondary";
      result.push(makeChip(displayName, "Membership Type", "membership-type", () => {
        providerHubContext.setFilter!("membershipType", providerHubContext.initialFilterState!.membershipType);
      }));
    }

    // Provider Source
    for (const type of filterState.providerNetworkTypes ?? []) {
      result.push(makeChip(providerNetworkTypeDisplayName(type), "Provider Source", `provider-source-${type}`, () => {
        providerHubContext.setFilter!("providerNetworkTypes", filterState.providerNetworkTypes.filter((t) => t !== type));
      }));
    }

    // Service Area — grouped by county status
    const regionSetIdToStatus = new Map<number, RegionSetStatusType>();
    for (const [status, ids] of providerHubContext.statusToRegionSet) {
      for (const id of ids) regionSetIdToStatus.set(id, status);
    }

    const countyChips: FilterChipDescriptor[] = [];
    for (const id of filterState.regionSetIds ?? []) {
      let name = String(id);
      for (const sa of (providerHubContext.serviceAreaOptions ?? [])) {
        const found = (sa.children ?? []).find((c) => String(c.key) === String(id));
        if (found) { name = `${found.name}, ${sa.name}`; break; }
      }
      const status = regionSetIdToStatus.get(id);
      countyChips.push(makeChip(name, "Service Area", `service-area-${id}`, () => {
        providerHubContext.setFilter!("regionSetIds", filterState.regionSetIds.filter((r) => r !== id));
      }, status ? `${status} Counties` : undefined));
    }
    countyChips.sort((a, b) => {
      const order = (subgroup: string | undefined) => {
        const idx = SERVICE_AREA_STATUS_DISPLAY_ORDER.findIndex(s => `${s} Counties` === subgroup);
        return idx === -1 ? SERVICE_AREA_STATUS_DISPLAY_ORDER.length : idx;
      };
      return order(a.subgroup) - order(b.subgroup);
    });
    result.push(...countyChips);

    // Specialty
    for (const item of filterState.specialtyGroupCodes ?? []) {
      result.push(makeChip(item.name, "Specialty Group", `specialty-${item.key}`, () => {
        providerHubContext.setFilter!("specialtyGroupCodes", filterState.specialtyGroupCodes.filter((s) => s.key !== item.key));
      }));
    }

    // Access Points (range)
    if (filterState.accessPointCountMin !== providerHubContext.defaultAccessPointsRangeMin || filterState.accessPointCountMax !== providerHubContext.defaultAccessPointsRangeMax) {
      result.push(makeChip(`${filterState.accessPointCountMin} - ${filterState.accessPointCountMax}`, "Access Points", "access-points", () => {
        providerHubContext.setFilter!("accessPointCountMin", providerHubContext.defaultAccessPointsRangeMin!);
        providerHubContext.setFilter!("accessPointCountMax", providerHubContext.defaultAccessPointsRangeMax!);
      }));
    }

    const isAggregateView = filterState.searchByOption === ProviderSearchByEnum.ContractEntity
      || filterState.viewByOption === ProviderViewByEnum.Organization
      || filterState.viewByOption === ProviderViewByEnum.Hospital;

    if (providerHubContext.showPciFilters) {
      if (!isAggregateView) {
        for (const item of filterState.topProcedures ?? []) {
          result.push(makeChip(item.name, "Top Procedures Search", `top-procedures-${item.key}`, () => {
            providerHubContext.setFilter!("topProcedures", filterState.topProcedures.filter((v) => v.key !== item.key));
          }));
        }

        for (const item of filterState.topConditions ?? []) {
          result.push(makeChip(item.name, "Top Conditions Search", `top-conditions-${item.key}`, () => {
            providerHubContext.setFilter!("topConditions", filterState.topConditions.filter((v) => v.key !== item.key));
          }));
        }

        for (const item of filterState.topPrescriptions ?? []) {
          result.push(makeChip(item.name, "Top Prescriptions Search", `top-prescriptions-${item.key}`, () => {
            providerHubContext.setFilter!("topPrescriptions", filterState.topPrescriptions.filter((v) => v.key !== item.key));
          }));
        }
      }

      if (isAggregateView) {
        // Effectiveness range (aggregate views)
        if (filterState.effectivenessMin !== providerHubContext.defaultEffectivenessRangeMin || filterState.effectivenessMax !== providerHubContext.defaultEffectivenessRangeMax) {
          result.push(makeChip(`${filterState.effectivenessMin} - ${filterState.effectivenessMax}`, "Effectiveness Range", "effectiveness-range", () => {
            providerHubContext.setFilter!("effectivenessMin", providerHubContext.defaultEffectivenessRangeMin);
            providerHubContext.setFilter!("effectivenessMax", providerHubContext.defaultEffectivenessRangeMax);
          }));
        }

        // Efficiency range (aggregate views)
        if (filterState.efficiencyMin !== providerHubContext.defaultEfficiencyRangeMin || filterState.efficiencyMax !== providerHubContext.defaultEfficiencyRangeMax) {
          result.push(makeChip(`${filterState.efficiencyMin} - ${filterState.efficiencyMax}`, "Efficiency Range", "efficiency-range", () => {
            providerHubContext.setFilter!("efficiencyMin", providerHubContext.defaultEfficiencyRangeMin);
            providerHubContext.setFilter!("efficiencyMax", providerHubContext.defaultEfficiencyRangeMax);
          }));
        }

        // "Include Unknown" excluded (aggregate views) - dismissing re-includes unscored entities
        if (filterState.includeUnknownEffectiveness === false) {
          result.push(makeChip("Excludes Unknown", "Effectiveness", "effectiveness-exclude-unknown", () => {
            providerHubContext.setFilter!("includeUnknownEffectiveness", true);
          }));
        }
        if (filterState.includeUnknownEfficiency === false) {
          result.push(makeChip("Excludes Unknown", "Efficiency", "efficiency-exclude-unknown", () => {
            providerHubContext.setFilter!("includeUnknownEfficiency", true);
          }));
        }
      } else {
        // Effectiveness
        for (const val of filterState.effectiveness ?? []) {
          result.push(makeChip(scoreFilterValueToDisplay(val), "Effectiveness", `effectiveness-${val}`, () => {
            providerHubContext.setFilter!("effectiveness", filterState.effectiveness!.filter((v) => v !== val));
          }));
        }

        // Efficiency
        for (const val of filterState.efficiency ?? []) {
          result.push(makeChip(scoreFilterValueToDisplay(val), "Efficiency", `efficiency-${val}`, () => {
            providerHubContext.setFilter!("efficiency", filterState.efficiency!.filter((v) => v !== val));
          }));
        }
      }

      // Location Confidence and claims-volume / new-patient-claims chips are
      // individual-Provider-search filters only; they are not shown for the
      // Contract Entity / Organization / Hospital (aggregate) views.
      if (!isAggregateView) {
        // Location Confidence
        if (providerHubContext.showLocationConfidenceFilter) {
          for (const item of filterState.locationConfidence ?? []) {
            result.push(makeChip(item.name, "Location Confidence", `location-confidence-${item.key}`, () => {
              providerHubContext.setFilter!("locationConfidence", filterState.locationConfidence!.filter((v) => v.key !== item.key));
            }));
          }
        }

        // Medicaid Claims Volume
        for (const item of filterState.medicaidClaimsVolume ?? []) {
          result.push(makeChip(item.name, "Medicaid Claims", `medicaid-claims-${item.key}`, () => {
            providerHubContext.setFilter!("medicaidClaimsVolume", filterState.medicaidClaimsVolume!.filter((v) => v.key !== item.key));
          }));
        }

        // Medicaid New Patient Claims
        for (const item of filterState.medicaidNewPatientIndicator ?? []) {
          result.push(makeChip(item.name, "Medicaid New Patient Claims", `medicaid-new-patient-${item.key}`, () => {
            providerHubContext.setFilter!("medicaidNewPatientIndicator", filterState.medicaidNewPatientIndicator!.filter((v) => v.key !== item.key));
          }));
        }

        // Medicare Claims Volume
        for (const item of filterState.medicareClaimsVolume ?? []) {
          result.push(makeChip(item.name, "Medicare Claims", `medicare-claims-${item.key}`, () => {
            providerHubContext.setFilter!("medicareClaimsVolume", filterState.medicareClaimsVolume!.filter((v) => v.key !== item.key));
          }));
        }

        // Medicare New Patient Claims
        for (const item of filterState.medicareNewPatientIndicator ?? []) {
          result.push(makeChip(item.name, "Medicare New Patient Claims", `medicare-new-patient-${item.key}`, () => {
            providerHubContext.setFilter!("medicareNewPatientIndicator", filterState.medicareNewPatientIndicator!.filter((v) => v.key !== item.key));
          }));
        }
      }

      if (providerHubContext.showMotiveScoreFilters) {
        if (isAggregateView) {
          // Motive Score range (aggregate views)
          if (filterState.motiveScoreMin !== providerHubContext.defaultMotiveScoreRangeMin || filterState.motiveScoreMax !== providerHubContext.defaultMotiveScoreRangeMax) {
            result.push(makeChip(`${filterState.motiveScoreMin} - ${filterState.motiveScoreMax}`, "Motive Tier Range", "motive-score-range", () => {
              providerHubContext.setFilter!("motiveScoreMin", providerHubContext.defaultMotiveScoreRangeMin!);
              providerHubContext.setFilter!("motiveScoreMax", providerHubContext.defaultMotiveScoreRangeMax!);
            }));
          }

          if (filterState.includeUnknownMotiveScore === false) {
            result.push(makeChip("Excludes Unknown", "Motive Tier", "motive-score-exclude-unknown", () => {
              providerHubContext.setFilter!("includeUnknownMotiveScore", true);
            }));
          }
        } else {
          // Motive Score
          for (const val of filterState.motiveScore ?? []) {
            result.push(makeChip(scoreFilterValueToDisplay(val), "Motive Tier", `motive-score-${val}`, () => {
              providerHubContext.setFilter!("motiveScore", filterState.motiveScore!.filter((v) => v !== val));
            }));
          }
        }
      }

      if (!isAggregateView) {
        // Total Claims Volume
        for (const item of filterState.totalClaimsVolume ?? []) {
          result.push(makeChip(item.name, "Total Claims Volume", `total-claims-${item.key}`, () => {
            providerHubContext.setFilter!("totalClaimsVolume", filterState.totalClaimsVolume!.filter((v) => v.key !== item.key));
          }));
        }

        // Total New Patient Claims
        for (const item of filterState.totalNewPatientIndicator ?? []) {
          result.push(makeChip(item.name, "Total New Patient Claims", `total-new-patient-${item.key}`, () => {
            providerHubContext.setFilter!("totalNewPatientIndicator", filterState.totalNewPatientIndicator!.filter((v) => v.key !== item.key));
          }));
        }

        // Commercial Claims Volume
        for (const item of filterState.commercialClaimsVolume ?? []) {
          result.push(makeChip(item.name, "Commercial Claims", `commercial-claims-${item.key}`, () => {
            providerHubContext.setFilter!("commercialClaimsVolume", filterState.commercialClaimsVolume!.filter((v) => v.key !== item.key));
          }));
        }

        // Commercial New Patient Claims
        for (const item of filterState.commercialNewPatientIndicator ?? []) {
          result.push(makeChip(item.name, "Commercial New Patient Claims", `commercial-new-patient-${item.key}`, () => {
            providerHubContext.setFilter!("commercialNewPatientIndicator", filterState.commercialNewPatientIndicator!.filter((v) => v.key !== item.key));
          }));
        }

        if (providerHubContext.showTriCareFilters) {
          for (const item of filterState.tricareClaimsVolume ?? []) {
            result.push(makeChip(item.name, "TRICARE Claims", `tricare-claims-${item.key}`, () => {
              providerHubContext.setFilter!("tricareClaimsVolume", filterState.tricareClaimsVolume!.filter((v) => v.key !== item.key));
            }));
          }
          for (const item of filterState.tricareNewPatientIndicator ?? []) {
            result.push(makeChip(item.name, "TRICARE New Patient Claims", `tricare-new-patient-${item.key}`, () => {
              providerHubContext.setFilter!("tricareNewPatientIndicator", filterState.tricareNewPatientIndicator!.filter((v) => v.key !== item.key));
            }));
          }
        }

        if (providerHubContext.showVaFilters) {
          for (const item of filterState.vaClaimsVolume ?? []) {
            result.push(makeChip(item.name, "VA Claims", `va-claims-${item.key}`, () => {
              providerHubContext.setFilter!("vaClaimsVolume", filterState.vaClaimsVolume!.filter((v) => v.key !== item.key));
            }));
          }
          for (const item of filterState.vaNewPatientIndicator ?? []) {
            result.push(makeChip(item.name, "VA New Patient Claims", `va-new-patient-${item.key}`, () => {
              providerHubContext.setFilter!("vaNewPatientIndicator", filterState.vaNewPatientIndicator!.filter((v) => v.key !== item.key));
            }));
          }
        }
      }
    }

    if (providerHubContext.showCareJourneyFilters) {
      if (isAggregateView) {
        if (filterState.careJourneyCostScoreMin !== providerHubContext.defaultCareJourneyCostRangeMin || filterState.careJourneyCostScoreMax !== providerHubContext.defaultCareJourneyCostRangeMax) {
          result.push(makeChip(`${filterState.careJourneyCostScoreMin} - ${filterState.careJourneyCostScoreMax}`, "CareJourney Cost Range", "carejourney-cost-range", () => {
            providerHubContext.setFilter!("careJourneyCostScoreMin", providerHubContext.defaultCareJourneyCostRangeMin);
            providerHubContext.setFilter!("careJourneyCostScoreMax", providerHubContext.defaultCareJourneyCostRangeMax);
          }));
        }
        if (filterState.careJourneyQualityScoreMin !== providerHubContext.defaultCareJourneyQualityRangeMin || filterState.careJourneyQualityScoreMax !== providerHubContext.defaultCareJourneyQualityRangeMax) {
          result.push(makeChip(`${filterState.careJourneyQualityScoreMin} - ${filterState.careJourneyQualityScoreMax}`, "CareJourney Quality Range", "carejourney-quality-range", () => {
            providerHubContext.setFilter!("careJourneyQualityScoreMin", providerHubContext.defaultCareJourneyQualityRangeMin);
            providerHubContext.setFilter!("careJourneyQualityScoreMax", providerHubContext.defaultCareJourneyQualityRangeMax);
          }));
        }
        if (filterState.includeUnknownCareJourneyCostScore === false) {
          result.push(makeChip("Excludes Unknown", "CareJourney Cost", "carejourney-cost-exclude-unknown", () => {
            providerHubContext.setFilter!("includeUnknownCareJourneyCostScore", true);
          }));
        }
        if (filterState.includeUnknownCareJourneyQualityScore === false) {
          result.push(makeChip("Excludes Unknown", "CareJourney Quality", "carejourney-quality-exclude-unknown", () => {
            providerHubContext.setFilter!("includeUnknownCareJourneyQualityScore", true);
          }));
        }
      } else {
        for (const val of filterState.careJourneyCostScore ?? []) {
          result.push(makeChip(scoreFilterValueToDisplay(val), "CareJourney Cost", `carejourney-cost-${val}`, () => {
            providerHubContext.setFilter!("careJourneyCostScore", filterState.careJourneyCostScore!.filter((v) => v !== val));
          }));
        }
        for (const val of filterState.careJourneyQualityScore ?? []) {
          result.push(makeChip(scoreFilterValueToDisplay(val), "CareJourney Quality", `carejourney-quality-${val}`, () => {
            providerHubContext.setFilter!("careJourneyQualityScore", filterState.careJourneyQualityScore!.filter((v) => v !== val));
          }));
        }
      }
    }

    if (providerHubContext.showNetworkIntegrityFilters && !isAggregateView) {
      const niGroups: { key: keyof typeof filterState; title: string }[] = [
        { key: "networkIntegrityProjectOptionsExclusions", title: "Project Options Exclusions" },
        { key: "networkIntegrityCriticalAlerts", title: "Critical Alert" },
        { key: "networkIntegrityAttestationBasedAlerts", title: "Attestation-based Alerts" },
      ];
      for (const { key, title } of niGroups) {
        const values = filterState[key] as string[];
        for (const value of values ?? []) {
          result.push(makeChip(niHideLabels[value] ?? `Hide ${value}`, title, `${key}-${value}`, () => {
            providerHubContext.setFilter!(key, values.filter((v) => v !== value));
          }));
        }
      }

      if (filterState.networkIntegrityExcessAddressesMin !== providerHubContext.defaultExcessAddressesRangeMin || filterState.networkIntegrityExcessAddressesMax !== providerHubContext.defaultExcessAddressesRangeMax) {
        const maxLabel = filterState.networkIntegrityExcessAddressesMax === providerHubContext.defaultExcessAddressesRangeMax
          ? `${providerHubContext.defaultExcessAddressesRangeMax - 1}+`
          : `${filterState.networkIntegrityExcessAddressesMax}`;
        result.push(makeChip(`${filterState.networkIntegrityExcessAddressesMin} - ${maxLabel}`, "Number of Addresses", "excess-addresses-range", () => {
          providerHubContext.setFilter!("networkIntegrityExcessAddressesMin", providerHubContext.defaultExcessAddressesRangeMin!);
          providerHubContext.setFilter!("networkIntegrityExcessAddressesMax", providerHubContext.defaultExcessAddressesRangeMax!);
        }));
      }

      if (filterState.networkIntegrityExcessSpecialtiesMin !== providerHubContext.defaultExcessSpecialtiesRangeMin || filterState.networkIntegrityExcessSpecialtiesMax !== providerHubContext.defaultExcessSpecialtiesRangeMax) {
        const maxLabel = filterState.networkIntegrityExcessSpecialtiesMax === providerHubContext.defaultExcessSpecialtiesRangeMax
          ? `${providerHubContext.defaultExcessSpecialtiesRangeMax - 1}+`
          : `${filterState.networkIntegrityExcessSpecialtiesMax}`;
        result.push(makeChip(`${filterState.networkIntegrityExcessSpecialtiesMin} - ${maxLabel}`, "Number of Specialties", "excess-specialties-range", () => {
          providerHubContext.setFilter!("networkIntegrityExcessSpecialtiesMin", providerHubContext.defaultExcessSpecialtiesRangeMin!);
          providerHubContext.setFilter!("networkIntegrityExcessSpecialtiesMax", providerHubContext.defaultExcessSpecialtiesRangeMax!);
        }));
      }
    }

    // Gap Closure Potential (range)
    if (filterState.closeGapsMin !== providerHubContext.defaultCloseGapsRangeMin || filterState.closeGapsMax !== providerHubContext.defaultCloseGapsRangeMax) {
      result.push(makeChip(`${filterState.closeGapsMin} - ${filterState.closeGapsMax}`, "Gap Closure Potential", "gap-closure", () => {
        providerHubContext.setFilter!("closeGapsMin", providerHubContext.defaultCloseGapsRangeMin!);
        providerHubContext.setFilter!("closeGapsMax", providerHubContext.defaultCloseGapsRangeMax!);
      }));
    }

    // Servicing Provider Count range (Contract Entity and Organization view)
    if (filterState.servicingProviderCountMin !== providerHubContext.defaultServicingProviderCountMin || filterState.servicingProviderCountMax !== providerHubContext.defaultServicingProviderCountMax) {
      result.push(makeChip(`${filterState.servicingProviderCountMin} - ${filterState.servicingProviderCountMax}`, "Servicing Providers", "servicing-provider-count-range", () => {
        providerHubContext.setFilter!("servicingProviderCountMin", providerHubContext.defaultServicingProviderCountMin);
        providerHubContext.setFilter!("servicingProviderCountMax", providerHubContext.defaultServicingProviderCountMax);
      }));
    }

    // Provider Action Status
    for (const status of filterState.providerActionStatuses ?? []) {
      result.push(makeChip(
        status === null ? "No Actions Applied" : ProviderActionStatusEnumToDisplay(status),
        "Provider Action Status",
        `provider-action-${status}`,
        () => { providerHubContext.setFilter!("providerActionStatuses", filterState.providerActionStatuses.filter((s) => s !== status)); },
      ));
    }

    return result;
  }, [providerHubContext]);

  return <QesActiveFiltersBar chips={chips} testIdPrefix="provider-hub__applied-filter" variant="accordion-style" />;
};
